import { authService } from "./authService";
import { getRuntimeGlobalState } from "./conversationRuntimeService";
function defaultGenerateThreadId(agentType) {
    return `${agentType}_thread_${Math.random().toString(36).slice(2, 8)}`;
}
export const cloneValue = (value) => {
    if (typeof structuredClone === "function") {
        return structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value));
};
export const findUiCutoffIndexForUserTurn = (messages, userTurnOrdinal) => {
    let seenUserMessages = 0;
    for (let index = 0; index < messages.length; index += 1) {
        if (messages[index].role === "user") {
            seenUserMessages += 1;
            if (seenUserMessages === userTurnOrdinal) {
                return index;
            }
        }
    }
    return -1;
};
export const sliceThreadToUserTurn = (messages, userTurnOrdinal) => {
    const collected = [];
    let seenUserMessages = 0;
    for (const message of messages) {
        collected.push(cloneValue(message));
        if (message.role === "user") {
            seenUserMessages += 1;
            if (seenUserMessages === userTurnOrdinal) {
                break;
            }
        }
    }
    if (seenUserMessages < userTurnOrdinal) {
        throw new Error("Selected user turn is no longer available in active thread history.");
    }
    return collected;
};
export const sliceThreadBeforeUserTurn = (messages, userTurnOrdinal) => {
    const sliced = sliceThreadToUserTurn(messages, userTurnOrdinal);
    if (sliced.length > 0 && sliced[sliced.length - 1].role === "user") {
        sliced.pop();
    }
    return sliced;
};
export const sliceUiBeforeUserTurn = (messages, userTurnOrdinal) => {
    const uiCutoffIndex = findUiCutoffIndexForUserTurn(messages, userTurnOrdinal);
    if (uiCutoffIndex < 0) {
        throw new Error("Selected user turn is no longer available in UI history.");
    }
    return cloneValue(messages.slice(0, uiCutoffIndex));
};
const buildForkConversationId = () => {
    const userId = authService.getCurrentUser()?._id;
    if (userId) {
        return `pconv_${userId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }
    return `fork_${Date.now()}`;
};
export async function forkConversationFromUserTurn(sourceConversationId, userTurnOrdinal, sourceUiMessages, options) {
    if (!sourceConversationId) {
        throw new Error("Cannot fork conversation: missing source conversation ID.");
    }
    if (!Number.isInteger(userTurnOrdinal) || userTurnOrdinal <= 0) {
        throw new Error("Cannot fork conversation: invalid user turn ordinal.");
    }
    if (!options.primaryAgentType) {
        throw new Error("Cannot fork conversation: missing primaryAgentType.");
    }
    const uiCutoffIndex = findUiCutoffIndexForUserTurn(sourceUiMessages, userTurnOrdinal);
    if (uiCutoffIndex < 0) {
        throw new Error("Cannot fork conversation: selected user turn not found in UI history.");
    }
    const globalState = getRuntimeGlobalState();
    const sourceConversation = globalState.getConversation(sourceConversationId);
    if (!sourceConversation) {
        throw new Error(`Cannot fork conversation: source conversation ${sourceConversationId} not found.`);
    }
    const sourceThreadManager = sourceConversation.agentRegistry.threadManager;
    const sourcePrimaryThread = sourceThreadManager.getMostRecentThread(options.primaryAgentType, sourceConversationId);
    if (!sourcePrimaryThread) {
        throw new Error("Cannot fork conversation: source primary thread not found.");
    }
    const forkedThreadMessages = sliceThreadToUserTurn(sourcePrimaryThread.messages, userTurnOrdinal);
    if (forkedThreadMessages.length === 0) {
        throw new Error("Cannot fork conversation: no thread messages available for selected turn.");
    }
    const newConversationId = buildForkConversationId();
    if (options.createConversation) {
        await options.createConversation(newConversationId, sourceConversation);
    }
    else {
        globalState.createConversation(newConversationId, sourceConversation.documentId || sourceConversation.presentationId, sourceConversation.conversationOrigin || "mainTaskpane");
    }
    const targetConversation = globalState.getConversation(newConversationId);
    if (!targetConversation) {
        throw new Error(`Cannot fork conversation: target conversation ${newConversationId} was not created.`);
    }
    const targetThreadManager = targetConversation.agentRegistry.threadManager;
    targetThreadManager.deleteThreadsForConversation(newConversationId);
    const clonedThread = {
        threadId: (options.generateThreadId || defaultGenerateThreadId)(sourcePrimaryThread.agentType),
        agentType: sourcePrimaryThread.agentType,
        conversationId: newConversationId,
        documentId: sourcePrimaryThread.documentId,
        messages: forkedThreadMessages,
        createdAt: Date.now(),
        lastActivityAt: Date.now(),
        tokenUsage: {
            used: 0,
            max: sourcePrimaryThread.tokenUsage.max,
        },
    };
    targetThreadManager.restoreThread(clonedThread, true);
    if (options.rebuildSearchHistoryFromMessages) {
        targetConversation.searchHistory = options.rebuildSearchHistoryFromMessages([{ messages: forkedThreadMessages }], targetConversation.documentId || targetConversation.presentationId);
    }
    const forkedUiMessages = cloneValue(sourceUiMessages.slice(0, uiCutoffIndex + 1));
    return {
        conversationId: newConversationId,
        messages: forkedUiMessages,
    };
}
